import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

const EditTimeTable = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth(); // Use AuthContext instead of Supabase auth
  
  // State for timetable data
  const [timetables, setTimetables] = useState([]);
  const [filteredTimetables, setFilteredTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUniversityId, setCurrentUniversityId] = useState(null);
  const [authError, setAuthError] = useState(null); // Add auth error state
  
  // State for search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    degree: '',
    year: '',
    semester: '',
    academicYear: '',
    dayOfWeek: '',
    classType: '',
    lecturer: '',
    module: '',
    group: ''
  });
  
  // State for dropdown options  
  const [degrees, setDegrees] = useState([]);
  const [lecturers, setLecturers] = useState([]);
  const [modules, setModules] = useState([]);
  const [venues, setVenues] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);  
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  
  // State for edit modal
  const [editingTimetable, setEditingTimetable] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    selectModules: '',
    startTime: '',
    endTime: '',
    dayOfWeek: '',
    venue: '',
    classType: '',
    lecturer: '',
    assistentLecturer: '',
    year: '',
    semester: '',
    academicYear: '',
    degree: [],
    groups: []
  });
  
  // Enhanced states for edit modal
  const [editAvailableGroups, setEditAvailableGroups] = useState({});
  const [editFilteredModules, setEditFilteredModules] = useState([]); 
  const [editFilteredLecturers, setEditFilteredLecturers] = useState({ primary: [], assistant: [] });
  
  // State for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTimetable, setDeletingTimetable] = useState(null);
  
  // Additional states for better functionality
  const [selectedTimetables, setSelectedTimetables] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false); 
  const [selectedGroup, setSelectedGroup] = useState('');
  
  useEffect(() => {
    if (currentUser) {
      fetchUniversityData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUniversityId) {
      fetchTimetables();
    }
  }, [currentUniversityId]);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, filters, timetables]);

  const fetchUniversityData = async () => {
    try {
      setLoading(true);
      setAuthError(null); // Reset auth error
      
      console.log('🔍 Starting fetchUniversityData in EditTimeTable...');
      
      // Check if user is authenticated through AuthContext
      if (!currentUser || !currentUser.email) {
        console.error('❌ No authenticated user found in AuthContext');
        setAuthError('Authentication failed. Please log in again.');
        setLoading(false);
        return;
      }

      console.log('✅ Current authenticated user from AuthContext:', currentUser.email);
      console.log('👤 Current user role:', currentUser.role);
      console.log('🆔 Current user ID:', currentUser.id);

      // Verify this is an admin user
      if (currentUser.role !== 'admin') {
        console.error('❌ User is not an admin:', currentUser.role);
        setAuthError('Access denied. This page is for administrators only.');
        setLoading(false);
        return;
      }

      // Get admin's university_id with more detailed logging
      console.log('🔍 Searching for admin with email:', currentUser.email);
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('university_id, id, email')
        .eq('email', currentUser.email)
        .single();

      console.log('📋 Admin query result:', { adminData, adminError });

      if (adminError || !adminData) {
        console.error('❌ Error fetching admin data:', adminError);
        console.error('❌ Admin not found for email:', currentUser.email);
        
        // Let's also check what admins exist in the database
        console.log('🔍 Checking all admins in database...');
        const { data: allAdmins, error: allAdminsError } = await supabase
          .from('admins')
          .select('email, university_id, active');
        
        console.log('📋 All admins in database:', allAdmins);
        console.log('📋 All admins query error:', allAdminsError);
        
        setAuthError('Admin access not found. Please contact your system administrator.');
        setLoading(false);
        return;
      }
      
      const universityId = adminData.university_id;
      setCurrentUniversityId(universityId);
      
      console.log('✅ Admin found!');
      console.log('🎓 Admin university ID:', universityId);
      console.log('👤 Admin ID:', adminData.id);
      console.log('📧 Admin email:', adminData.email);

      // Fetch degrees for filters
      const { data: degreesData, error: degreesError } = await supabase
        .from('degree_details')
        .select('id, degree_programme, degree_level')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('degree_programme');

      if (!degreesError) {
        setDegrees(degreesData || []);
      }

      // Fetch lecturers for filters
      const { data: lecturersData, error: lecturersError } = await supabase
        .from('staff')
        .select('id, name, staff_type')
        .eq('university_id', universityId)
        .eq('active', true)
        .in('staff_type', ['lecturer', 'professor', 'assistant_lecturer'])
        .order('name');

      if (!lecturersError) {
        setLecturers(lecturersData || []);
      }

      // Fetch modules for filters
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('id, module_name, module_code, semester')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('module_name');

      if (!modulesError) {
        setModules(modulesData || []);
      }

      // Fetch unique academic years from timetables
      const { data: academicYearsData, error: academicYearsError } = await supabase
        .from('timetables')
        .select('academic_year')
        .eq('university_id', universityId)
        .eq('active', true);

      if (!academicYearsError) {
        const dbYears = [...new Set(academicYearsData.map(t => t.academic_year).filter(Boolean))];
        
        // Add standard academic years to ensure they're always available
        const standardYears = ['2023-2024', '2024-2025', '2025-2026', '2026-2027', '2027-2028'];
        
        // Combine database years with standard years and remove duplicates
        const allYears = [...new Set([...dbYears, ...standardYears])];
        
        setAcademicYears(allYears.sort());
      }

      // Fetch all groups for the university
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select(`
          id,
          group_name,
          degree_details_id,
          year,
          semester,
          academic_year,
          degree_details:degree_details_id (
            id,
            degree_programme,
            degree_level
          )
        `)
        .eq('university_id', universityId)
        .eq('active', true)
        .order('group_name');

      if (!groupsError) {
        setGroups(groupsData || []);
      }

    } catch (error) {
      console.error('Error in fetchUniversityData:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimetables = async () => {
    try {
      setLoading(true);
      
      if (!currentUniversityId) {
        console.warn('⚠️ No university ID available, skipping timetables fetch');
        return;
      }
      
      console.log('🔍 Fetching timetables for university ID:', currentUniversityId);
      
      const { data: timetablesData, error: timetablesError } = await supabase
        .from('timetables')
        .select(`
          *,
          modules:module_id (
            id,
            module_name,
            module_code
          ),
          lecturer:lecturer_id (
            id,
            name,
            staff_type
          ),
          assistant_lecturer:assistant_lecturer_id (
            id,
            name,
            staff_type
          )
        `)
        .eq('university_id', currentUniversityId)
        .eq('active', true)
        .order('day_of_week')
        .order('start_time');

      console.log('📋 Timetables query result:', { timetablesData, timetablesError });

      if (timetablesError) {
        console.error('❌ Error fetching timetables:', timetablesError);
      } else {
        console.log('✅ Fetched timetables for university:', timetablesData?.length || 0, 'records');
        
        // Log each timetable's university ID for verification
        (timetablesData || []).forEach((timetable, index) => {
          console.log(`📝 Timetable ${index + 1}: Module "${timetable.modules?.module_name}" (University ID: ${timetable.university_id})`);
        });
        // Process the data and fetch group information separately
        const processedTimetables = [];
        
        for (const timetable of (timetablesData || [])) {
          // Fetch groups for this timetable
          const { data: groupsData, error: groupsError } = await supabase
            .from('groups')
            .select(`
              id,
              group_name,
              degree_details:degree_details_id (
                id,
                degree_programme,
                degree_level
              )
            `)
            .in('id', timetable.group_ids || [])
            .eq('active', true);

          // Fetch degree details for this timetable
          const { data: degreeData, error: degreeError } = await supabase
            .from('degree_details')
            .select('id, degree_programme, degree_level')
            .in('id', timetable.degree_details_ids || [])
            .eq('active', true);

          let groupNames = '';
          let degreeNames = '';
          
          if (!groupsError && groupsData) {
            groupNames = groupsData.map(g => g.group_name).join(', ');
          }

          if (!degreeError && degreeData) {
            degreeNames = degreeData.map(d => `${d.degree_programme} (${d.degree_level})`).join(', ');
          }

          processedTimetables.push({
            ...timetable,
            groupNames,
            degreeNames,
            groups: groupsData || [],
            degrees: degreeData || []
          });
        }
        
        // Let's also check if there are timetables from other universities (for debugging)
        console.log('🔍 Checking timetables from other universities (for debugging)...');
        const { data: allTimetablesData, error: allTimetablesError } = await supabase
          .from('timetables')
          .select('id, university_id, modules:module_id(module_name), universities:university_id(name)')
          .eq('active', true);
        
        console.log('📋 All timetables in database:', allTimetablesData?.length || 0);
        if (allTimetablesData) {
          const otherUniversityTimetables = allTimetablesData.filter(t => t.university_id !== currentUniversityId);
          console.log('🚨 Timetables from other universities:', otherUniversityTimetables.length);
          if (otherUniversityTimetables.length > 0) {
            console.log('🚨 Sample other university timetables:', otherUniversityTimetables.slice(0, 3));
          }
        }
        
        setTimetables(processedTimetables);
      }
    } catch (error) {
      console.error('Error in fetchTimetables:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...timetables];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(timetable => 
        timetable.modules?.module_name?.toLowerCase().includes(query) ||
        timetable.modules?.module_code?.toLowerCase().includes(query) ||
        timetable.lecturer?.name?.toLowerCase().includes(query) ||
        timetable.assistant_lecturer?.name?.toLowerCase().includes(query) ||
        timetable.venue?.toLowerCase().includes(query) ||
        timetable.groupNames?.toLowerCase().includes(query) ||
        timetable.degreeNames?.toLowerCase().includes(query) ||
        timetable.class_type?.toLowerCase().includes(query)
      );
    }

    if (filters.module) {
      filtered = filtered.filter(timetable => 
        timetable.module_id === filters.module
      );
    }

    // Apply degree filter
    if (filters.degree) {
      filtered = filtered.filter(timetable => 
        timetable.degree_details_ids && timetable.degree_details_ids.includes(filters.degree)
      );
    }

    // Apply group filter
    if (filters.group) {
      filtered = filtered.filter(timetable => 
        timetable.group_ids && timetable.group_ids.includes(filters.group)
      );
    }

    // Apply filters
    if (filters.year) {
      const yearNumber = parseInt(filters.year.replace('Year ', ''));
      filtered = filtered.filter(timetable => timetable.year === yearNumber);
    }

    if (filters.semester) {
      const semesterNumber = parseInt(filters.semester.replace('Semester ', ''));
      filtered = filtered.filter(timetable => timetable.semester === semesterNumber);
    }

    if (filters.academicYear) {
      filtered = filtered.filter(timetable => timetable.academic_year === filters.academicYear);
    }

    if (filters.dayOfWeek) {
      filtered = filtered.filter(timetable => timetable.day_of_week === filters.dayOfWeek);
    }

    if (filters.classType) {
      filtered = filtered.filter(timetable => timetable.class_type === filters.classType);
    }

    if (filters.lecturer) {
      filtered = filtered.filter(timetable => 
        timetable.lecturer_id === filters.lecturer || 
        timetable.assistant_lecturer_id === filters.lecturer
      );
    }

    setFilteredTimetables(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };

      // Reset group filter when dependent filters change
      if (['degree', 'year', 'semester', 'academicYear'].includes(field)) {
        newFilters.group = '';
      }

      return newFilters;
    });
  };

  const updateFilteredGroups = () => {
    let filtered = [...groups];

    // Apply filters based on selected criteria
    if (filters.degree) {
      filtered = filtered.filter(group => group.degree_details_id === filters.degree);
    }

    if (filters.year) {
      const yearNumber = parseInt(filters.year.replace('Year ', ''));
      filtered = filtered.filter(group => group.year === yearNumber);
    }

    if (filters.semester) {
      const semesterNumber = parseInt(filters.semester.replace('Semester ', ''));
      filtered = filtered.filter(group => group.semester === semesterNumber);
    }

    if (filters.academicYear) {
      filtered = filtered.filter(group => group.academic_year === filters.academicYear);
    }

    setFilteredGroups(filtered);
  };

  const clearFilters = () => {
    setFilters({
      degree: '',
      year: '',
      semester: '',
      academicYear: '',
      dayOfWeek: '',
      classType: '',
      lecturer: '',
      module: '',
      group: ''
    });
    setSearchQuery('');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      // Extract time directly from UTC timestamp without timezone conversion
      // Since we store as 1970-01-01T14:30:00.000Z, we need to get UTC hours/minutes
      const date = new Date(timeString);
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      
      // Format as 12-hour time
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  const handleEditClick = async (timetable) => {
    setEditingTimetable(timetable);
    
    // Pre-fill form data with existing timetable data
    // Extract UTC time to match what was originally selected
    const startTime = new Date(timetable.start_time);
    const endTime = new Date(timetable.end_time);
    
    setEditFormData({
      selectModules: timetable.module_id,
      startTime: `${startTime.getUTCHours().toString().padStart(2, '0')}:${startTime.getUTCMinutes().toString().padStart(2, '0')}`,
      endTime: `${endTime.getUTCHours().toString().padStart(2, '0')}:${endTime.getUTCMinutes().toString().padStart(2, '0')}`,
      dayOfWeek: timetable.day_of_week,
      venue: timetable.venue,
      classType: timetable.class_type,
      lecturer: timetable.lecturer_id,
      assistentLecturer: timetable.assistant_lecturer_id || '',
      year: timetable.year,
      semester: timetable.semester,
      academicYear: timetable.academic_year,
      degree: timetable.degree_details_ids || [],
      groups: timetable.group_ids || []
    });
    
    // Fetch available modules for the selected degrees
    if (timetable.degree_details_ids && timetable.degree_details_ids.length > 0) {
      await fetchModulesForDegrees(timetable.degree_details_ids, timetable.year, timetable.semester);
    }
    
    // Fetch lecturers for the selected module
    await fetchLecturersForModule(timetable.module_id);
    
    // Fetch groups for the selected degrees
    if (timetable.degree_details_ids && timetable.degree_details_ids.length > 0) {
      await fetchGroupsForDegrees(timetable.degree_details_ids);
    }
    
    setShowEditModal(true);
  };

  const fetchLecturersForModule = async (moduleId) => {
    try {
      if (!currentUniversityId || !moduleId) return;

      const { data: lecturerData, error: lecturerError } = await supabase
        .from('staff_courses')
        .select(`
          staff_id,
          can_teach_as_primary,
          can_teach_as_assistant,
          staff:staff_id (
            id,
            name,
            staff_type,
            university_id,
            active
          )
        `)
        .eq('module_id', moduleId)
        .eq('staff.university_id', currentUniversityId)
        .eq('staff.active', true)
        .in('staff.staff_type', ['lecturer', 'professor', 'assistant_lecturer']);

      if (!lecturerError) {
        const availableLecturers = {
          primary: [],
          assistant: []
        };

        (lecturerData || []).forEach(course => {
          if (course.staff) {
            if (course.can_teach_as_primary) {
              availableLecturers.primary.push(course.staff);
            }
            if (course.can_teach_as_assistant) {
              availableLecturers.assistant.push(course.staff);
            }
          }
        });

        // Remove duplicates and sort by name
        availableLecturers.primary = [...new Map(availableLecturers.primary.map(l => [l.id, l])).values()]
          .sort((a, b) => a.name.localeCompare(b.name));
        availableLecturers.assistant = [...new Map(availableLecturers.assistant.map(l => [l.id, l])).values()]
          .sort((a, b) => a.name.localeCompare(b.name));

        setEditFilteredLecturers(availableLecturers);
      }
    } catch (error) {
      console.error('Error in fetchLecturersForModule:', error);
    }
  };

  const fetchModulesForDegrees = async (degreeIds, year, semester) => {
    try {
      if (!currentUniversityId || !degreeIds || degreeIds.length === 0) return;

      const { data: moduleData, error } = await supabase
        .from('module_degree_mappings')
        .select(`
          module_id,
          modules:module_id (
            id,
            module_name,
            module_code,
            semester,
            university_id,
            active
          )
        `)
        .in('degree_details_id', degreeIds)
        .eq('year', year)
        .eq('modules.university_id', currentUniversityId)
        .eq('modules.semester', semester)
        .eq('modules.active', true);

      if (!error) {
        const uniqueModules = [];
        const moduleIds = new Set();
        
        (moduleData || []).forEach(mapping => {
          if (mapping.modules && !moduleIds.has(mapping.modules.id)) {
            moduleIds.add(mapping.modules.id);
            uniqueModules.push(mapping.modules);
          }
        });
        
        uniqueModules.sort((a, b) => a.module_code.localeCompare(b.module_code));
        setEditFilteredModules(uniqueModules);
      }
    } catch (error) {
      console.error('Error fetching modules for degrees:', error);
    }
  };

  const fetchGroupsForDegrees = async (degreeIds) => {
    try {
      if (!currentUniversityId || !degreeIds || degreeIds.length === 0) return;

      const { data: groupsData, error } = await supabase
        .from('groups')
        .select('id, group_name, degree_details_id')
        .in('degree_details_id', degreeIds)
        .eq('university_id', currentUniversityId)
        .eq('active', true)
        .order('group_name');

      if (!error) {
        const groupsByDegree = {};
        (groupsData || []).forEach(group => {
          if (!groupsByDegree[group.degree_details_id]) {
            groupsByDegree[group.degree_details_id] = [];
          }
          groupsByDegree[group.degree_details_id].push(group);
        });
        setEditAvailableGroups(groupsByDegree);
      }
    } catch (error) {
      console.error('Error fetching groups for degrees:', error);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset dependent fields when certain fields change
      ...(field === 'selectModules' ? { lecturer: '', assistentLecturer: '' } : {}),
      ...(field === 'degree' ? { groups: [], selectModules: '', lecturer: '', assistentLecturer: '' } : {}),
      ...(field === 'year' || field === 'semester' ? { selectModules: '', lecturer: '', assistentLecturer: '' } : {})
    }));

    // Fetch dependent data when certain fields change
    if (field === 'selectModules' && value) {
      fetchLecturersForModule(value);
    }
    
    if (field === 'degree' && value.length > 0) {
      fetchModulesForDegrees(value, editFormData.year, editFormData.semester);
      fetchGroupsForDegrees(value);
    }
    
    if ((field === 'year' || field === 'semester') && editFormData.degree.length > 0) {
      const year = field === 'year' ? value : editFormData.year;
      const semester = field === 'semester' ? value : editFormData.semester;
      fetchModulesForDegrees(editFormData.degree, year, semester);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // Validate required fields
      if (!editFormData.selectModules || !editFormData.startTime || !editFormData.endTime || 
          !editFormData.dayOfWeek || !editFormData.venue || !editFormData.classType || !editFormData.lecturer) {
        alert('Please fill in all required fields.');
        setLoading(false);
        return;
      }

      // Always store time as UTC, matching the selected value (no local timezone shift)
      // Use a fixed date (1970-01-01) for time-only semantics
      const [startHour, startMinute] = editFormData.startTime.split(':');
      const [endHour, endMinute] = editFormData.endTime.split(':');

      // Construct UTC date string for 1970-01-01 at selected time
      const startDateTime = new Date(Date.UTC(1970, 0, 1, parseInt(startHour), parseInt(startMinute), 0, 0));
      const endDateTime = new Date(Date.UTC(1970, 0, 1, parseInt(endHour), parseInt(endMinute), 0, 0));

      const updateData = {
        module_id: editFormData.selectModules,
        lecturer_id: editFormData.lecturer,
        assistant_lecturer_id: editFormData.assistentLecturer || null,
        day_of_week: editFormData.dayOfWeek,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        venue: editFormData.venue,
        class_type: editFormData.classType,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('timetables')
        .update(updateData)
        .eq('id', editingTimetable.id);

      if (updateError) {
        console.error('Error updating timetable:', updateError);
        alert('Error updating timetable entry. Please try again.');
        setLoading(false);
        return;
      }

      alert('Timetable entry updated successfully!');
      setShowEditModal(false);
      setEditingTimetable(null);
      await fetchTimetables(); // Refresh the list

    } catch (error) {
      console.error('Error in handleEditSubmit:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (timetable) => {
    setDeletingTimetable(timetable);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);

      const { error: deleteError } = await supabase
        .from('timetables')
        .update({ active: false, updated_at: new Date().toISOString() })
        .eq('id', deletingTimetable.id);

      if (deleteError) {
        console.error('Error deleting timetable:', deleteError);
        alert('Error deleting timetable entry. Please try again.');
        setLoading(false);
        return;
      }

      alert('Timetable entry deleted successfully!');
      setShowDeleteModal(false);
      setDeletingTimetable(null);
      await fetchTimetables(); // Refresh the list

    } catch (error) {
      console.error('Error in handleDeleteConfirm:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add useEffect to fetch groups when dependencies change
  useEffect(() => {
    updateFilteredGroups();
  }, [filters.degree, filters.semester, filters.year, filters.academicYear, groups]);

  return (
    <>
      {/* Custom styles */}
      <style>
        {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }
          
          .modal-content {
            background: white;
            border-radius: 15px;
            padding: 30px;
            max-width: 90vw;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          }
          
          .table-container {
            overflow-x: auto;
            border-radius: 15px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          }
          
          .timetable-table {
            width: 100%;
            border-collapse: collapse;
            background: white;
          }
          
          .timetable-table th {
            background: linear-gradient(135deg, #F04915 0%, #FF6B35 100%);
            color: white;
            padding: 16px 12px;
            text-align: left;
            font-family: 'Outfit';
            font-weight: 600;
            font-size: 14px;
            border: none;
            white-space: nowrap;
          }
          
          .timetable-table td {
            padding: 16px 12px;
            border-bottom: 1px solid #E5E7EB;
            font-family: 'Outfit';
            font-size: 13px;
            vertical-align: top;
          }
          
          .timetable-table tbody tr:hover {
            background-color: #FEF3F2;
          }
          
          .action-button {
            padding: 6px 12px;
            border: none;
            border-radius: 6px;
            font-family: 'Outfit';
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            margin-right: 5px;
          }
          
          .edit-button {
            background: linear-gradient(135deg, #10B981 0%, #059669 100%);
            color: white;
          }
          
          .edit-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          }
          
          .delete-button {
            background: linear-gradient(135deg, #DC2626 0%, #B91C1C 100%);
            color: white;
          }
          
          .delete-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
          }
          
          select option:hover {
            background-color: #FEF3F2 !important;
            color: #F04915 !important;
          }
          select option:checked {
            background-color: #F04915 !important;
            color: white !important;
          }
        `}
      </style>
      
      <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
        <Sidebar />
        
        <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
          <Header />
          
          {/* Page Title */}
          <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
            Edit Time Tables
          </h1>

          {/* Main Container */}
          <div style={{margin: '20px 40px'}}>
            {/* Search and Filter Section */}
            <div style={{
              background: '#F04915',
              padding: '20px',
              borderTopLeftRadius: 15,
              borderTopRightRadius: 15
            }}>
              <h2 style={{color: 'white', fontSize: 20, fontFamily: 'Outfit', fontWeight: '600', margin: '0 0 20px 0'}}>
                Search & Filter Time Tables
              </h2>

              {/* Search Bar */}
              <div style={{marginBottom: '20px'}}>
                <input
                  type="text"
                  placeholder="Search by module name, code, lecturer, venue, or group..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                />
              </div>

              {/* Filters */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '15px'
              }}>
                <select
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Years</option>
                  <option value="Year 1">Year 1</option>
                  <option value="Year 2">Year 2</option>
                  <option value="Year 3">Year 3</option>
                  <option value="Year 4">Year 4</option>
                </select>

                <select
                  value={filters.semester}
                  onChange={(e) => handleFilterChange('semester', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Semesters</option>
                  <option value="Semester 1">Semester 1</option>
                  <option value="Semester 2">Semester 2</option>
                </select>

                <select
                  value={filters.academicYear}
                  onChange={(e) => handleFilterChange('academicYear', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Academic Years</option>
                  {academicYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.dayOfWeek}
                  onChange={(e) => handleFilterChange('dayOfWeek', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Days</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>

                <select
                  value={filters.classType}
                  onChange={(e) => handleFilterChange('classType', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Class Types</option>
                  <option value="Lecture">Lecture</option>
                  <option value="Tutorial">Tutorial</option>
                  <option value="Practical">Practical</option>
                  <option value="Laboratory">Laboratory</option>
                  <option value="Workshop">Workshop</option>
                </select>

                <select
                  value={filters.module}
                  onChange={(e) => handleFilterChange('module', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Modules</option>
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.module_code} - {module.module_name}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.degree}
                  onChange={(e) => handleFilterChange('degree', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Degrees</option>
                  {degrees.map(degree => (
                    <option key={degree.id} value={degree.id}>
                      {degree.degree_programme} ({degree.degree_level})
                    </option>
                  ))}
                </select>

                <select
                  value={filters.lecturer}
                  onChange={(e) => handleFilterChange('lecturer', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Lecturers</option>
                  {lecturers.map(lecturer => (
                    <option key={lecturer.id} value={lecturer.id}>
                      {lecturer.name}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.group}
                  onChange={(e) => handleFilterChange('group', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                  disabled={!filters.degree && !filters.year && !filters.semester && !filters.academicYear}
                >
                  <option value="">
                    {(!filters.degree && !filters.year && !filters.semester && !filters.academicYear) 
                      ? 'Select filters to show groups' 
                      : 'All Groups'}
                  </option>
                  {filteredGroups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.group_name}
                    </option>
                  ))}
                </select>

                <button
                  onClick={clearFilters}
                  style={{
                    padding: '10px 16px',
                    background: 'white',
                    color: '#F04915',
                    border: '2px solid white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Outfit',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#FEF3F2';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'white';
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Results Section */}
            <div style={{
              background: '#FFFBF5',
              padding: '30px',
              borderBottomLeftRadius: 15,
              borderBottomRightRadius: 15,
              minHeight: '400px'
            }}>
              {loading ? (
                <div style={{
                  textAlign: 'center',
                  padding: '50px',
                  fontSize: '16px',
                  color: '#666',
                  fontFamily: 'Outfit'
                }}>
                  Loading timetables...
                </div>
              ) : authError ? (
                <div style={{
                  textAlign: 'center',
                  padding: '50px',
                  fontSize: '16px',
                  color: '#dc3545',
                  fontFamily: 'Outfit'
                }}>
                  {authError}
                </div>
              ) : filteredTimetables.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '50px',
                  fontSize: '16px',
                  color: '#666',
                  fontFamily: 'Outfit'
                }}>
                  {timetables.length === 0 ? 'No timetables found.' : 'No timetables match your search criteria.'}
                </div>
              ) : (
                <>
                  {/* Results Count */}
                  <div style={{
                    marginBottom: '20px',
                    fontSize: '14px',
                    color: '#666',
                    fontFamily: 'Outfit'
                  }}>
                    Showing {filteredTimetables.length} of {timetables.length} timetable entries
                  </div>

                  {/* Timetables Table */}
                  <div className="table-container">
                    <table className="timetable-table">
                      <thead>
                        <tr>
                          <th>Module</th>
                          <th>Day</th>
                          <th>Time</th>
                          <th>Venue</th>
                          <th>Class Type</th>
                          <th>Lecturer</th>
                          <th>Groups</th>
                          <th>Year/Semester</th>
                          <th>Academic Year</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTimetables.map((timetable) => (
                          <tr key={timetable.id}>
                            <td>
                              <div style={{ fontWeight: '600', color: '#1F2937' }}>
                                {timetable.modules?.module_code}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                {timetable.modules?.module_name}
                              </div>
                            </td>
                            <td>{timetable.day_of_week}</td>
                            <td>
                              <div>{formatTime(timetable.start_time)}</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                to {formatTime(timetable.end_time)}
                              </div>
                            </td>
                            <td>{timetable.venue}</td>
                            <td>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500',
                                backgroundColor: '#FEF3F2',
                                color: '#F04915'
                              }}>
                                {timetable.class_type}
                              </span>
                            </td>
                            <td>
                              <div style={{ fontWeight: '500' }}>
                                {timetable.lecturer?.name}
                              </div>
                              {timetable.assistant_lecturer && (
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                  Assistant: {timetable.assistant_lecturer.name}
                                </div>
                              )}
                            </td>
                            <td>
                              <div style={{ maxWidth: '150px' }}>
                                {timetable.groupNames || 'No groups'}
                              </div>
                            </td>
                            <td>
                              Year {timetable.year}, Semester {timetable.semester}
                            </td>
                            <td>{timetable.academic_year}</td>
                            <td>
                              <button
                                className="action-button edit-button"
                                onClick={() => handleEditClick(timetable)}
                                title="Edit Timetable"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                className="action-button delete-button"
                                onClick={() => handleDeleteClick(timetable)}
                                title="Delete Timetable"
                              >
                                🗑️ Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Back Button */}
            <div style={{display: 'flex', justifyContent: 'flex-start', marginTop: '20px'}}>
              <Link to="/timetable-management">
                <button 
                  type="button" 
                  style={{
                    width: 269, 
                    padding: '11px 20px', 
                    background: '#F04915', 
                    border: 'none', 
                    borderRadius: 8, 
                    color: 'white', 
                    fontSize: 16, 
                    fontFamily: 'Outfit', 
                    fontWeight: '500', 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#D63A0F';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#F04915';
                  }}
                >
                  Back to Time Table Management
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: '700px' }}>
            <h2 style={{
              fontSize: '24px',
              fontFamily: 'Outfit',
              fontWeight: '700',
              color: '#F04915',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Edit Timetable Entry
            </h2>

            <form onSubmit={handleEditSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Module *</label>
                  <select
                    value={editFormData.selectModules}
                    onChange={(e) => handleEditFormChange('selectModules', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                    required
                  >
                    <option value="">Select Module</option>
                    {editFilteredModules.map((module) => (
                      <option key={module.id} value={module.id}>
                        {module.module_code} - {module.module_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Start Time *</label>
                  <input
                    type="time"
                    value={editFormData.startTime}
                    onChange={(e) => handleEditFormChange('startTime', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>End Time *</label>
                  <input
                    type="time"
                    value={editFormData.endTime}
                    onChange={(e) => handleEditFormChange('endTime', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Day of Week *</label>
                  <select
                    value={editFormData.dayOfWeek}
                    onChange={(e) => handleEditFormChange('dayOfWeek', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                    required
                  >
                    <option value="">Select Day</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Venue *</label>
                  <input
                    type="text"
                    value={editFormData.venue}
                    onChange={(e) => handleEditFormChange('venue', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none'
                    }}
                    placeholder="e.g., Room 101, Lab A"
                    required
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Class Type *</label>
                  <select
                    value={editFormData.classType}
                    onChange={(e) => handleEditFormChange('classType', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                    required
                  >
                    <option value="">Select Class Type</option>
                    <option value="Lecture">Lecture</option>
                    <option value="Tutorial">Tutorial</option>
                    <option value="Practical">Practical</option>
                    <option value="Laboratory">Laboratory</option>
                    <option value="Workshop">Workshop</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Lecturer *</label>
                  <select
                    value={editFormData.lecturer}
                    onChange={(e) => handleEditFormChange('lecturer', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                    required
                  >
                    <option value="">Select Lecturer</option>
                    {editFilteredLecturers.primary.map((lecturer) => (
                      <option key={lecturer.id} value={lecturer.id}>
                        {lecturer.name} ({lecturer.staff_type})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Assistant Lecturer</label>
                  <select
                    value={editFormData.assistentLecturer}
                    onChange={(e) => handleEditFormChange('assistentLecturer', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select Assistant (Optional)</option>
                    {editFilteredLecturers.assistant.map((lecturer) => (
                      <option key={lecturer.id} value={lecturer.id}>
                        {lecturer.name} ({lecturer.staff_type})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    fontFamily: 'Outfit'
                  }}>Group</label>
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontFamily: 'Outfit',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                    disabled={!editFormData.degree.length || !editFormData.semester || !editFormData.year || !editFormData.academicYear}
                  >
                    <option value="">Select Group (Optional)</option>
                    {Object.values(editAvailableGroups).flat().map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.group_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '12px 30px',
                    background: loading ? '#ccc' : '#F04915',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px',
                    fontFamily: 'Outfit',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Updating...' : 'Update Timetable'}
                </button>

                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    padding: '12px 30px',
                    background: '#6B7280',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px',
                    fontFamily: 'Outfit',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: '500px' }}>
            <h2 style={{
              fontSize: '24px',
              fontFamily: 'Outfit',
              fontWeight: '700',
              color: '#DC2626',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Confirm Delete
            </h2>

            <p style={{
              fontSize: '16px',
              fontFamily: 'Outfit',
              color: '#374151',
              textAlign: 'center',
              marginBottom: '30px',
              lineHeight: '1.5'
            }}>
              Are you sure you want to delete this timetable entry?<br />
              <strong>{deletingTimetable?.modules?.module_code} - {deletingTimetable?.modules?.module_name}</strong><br />
              {deletingTimetable?.day_of_week} at {formatTime(deletingTimetable?.start_time)}
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={handleDeleteConfirm}
                disabled={loading}
                style={{
                  padding: '12px 30px',
                  background: loading ? '#ccc' : '#DC2626',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'Outfit',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Deleting...' : 'Yes, Delete'}
              </button>

              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '12px 30px',
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'Outfit',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditTimeTable;
